import React from "react";
import { Parser } from "json2csv";
import getSymbolFromCurrency from "currency-symbol-map";

import PlanManager from "./PlanManager";
import { PlanDetails } from "./components/PlanDetails";
import { CreatePlan } from "./components/CreatePlan";
import { PlanTrial } from "./components/PlanTrial";
import { getDivisor, getIntervalString } from "../../../utils/stripe-helpers";
import { MinimumTerm } from "./components/MinimumTerm";
import ActivityDrawer from "../../../parts/activity-drawer/ActivityDrawer";
import { CreateCoupon } from "./components/CreateCoupon";

const formatPlans = (plans = [], locations = []) => {
  return plans.map((plan) => {
    const {
      currency,
      features,
      metadata = {},
      recurring,
      unit_amount,
      nickname,
      facilikey = [],
    } = plan;
    const { access } = metadata;
    const symbol = getSymbolFromCurrency(currency);
    const divisor = getDivisor(currency);
    const price = unit_amount / divisor;
    const facilities = facilikey.map((value) => {
      const facility = locations.find(({ facilikey }) => facilikey === value);
      if (!facility) return "";
      return `${facility.gymName} (${facility.gymLocation})`;
    });
    return {
      program_name: nickname,
      features: features,
      price: `${symbol}${price} (${currency})`,
      primary_access: facilities?.[0] || "",
      secondary_access: facilities?.[1] || "",
      recurring: recurring ? "Yes" : "No",
      frequency: `(${getIntervalString(recurring)})`,
      user_type: access,
    };
  });
};

const PlanManagerController = async ({ load, locals, res, req, flash }) => {
  const { query } = req;
  const modal = query.modal || {};

  const loadPromises = [load("planManager")];
  if (modal.type === "create-coupon") {
    loadPromises.push(load("stripeCoupon"));
  }
  await Promise.all(loadPromises);

  if (query.copied) {
    flash("success", "Payment link copied to clipboard");
    return res.refreshWith({ copied: false });
  }

  let planObj;

  const { activeUser, planManager } = locals;

  if (query.plan && query.type) {
    if (query.type === "pull") {
      planObj = planManager.pullPlans.find((plan) => plan.id === query.plan);
    } else if (query.type === "push") {
      planObj = planManager.pushPlans.find((plan) => plan.id === query.plan);
    } else {
      planObj = planManager.pressPlans.find((plan) => plan.id === query.plan);
    }
  }

  const json2csvParser = new Parser();
  const locations = activeUser.pressCustomBranding?.gymLocations;
  const csvPress =
    planManager.pressPlans?.length > 0
      ? json2csvParser.parse(formatPlans(planManager.pressPlans, locations))
      : null;

  locals.modal = {
    "plan-details": (
      <PlanDetails
        plan={planObj}
        type={query.type}
        user={locals.activeUserToRequest}
      />
    ),
    "create-plan": (
      <CreatePlan
        type={query.type}
        plan={planObj}
        user={locals.activeUserToRequest}
        taxRates={planManager.taxRates}
      />
    ),
    "plan-trial": <PlanTrial plan={planObj} />,
    "plan-minimum-term": <MinimumTerm plan={planObj} />,
    "activity-feed": <ActivityDrawer />,
    "create-coupon": <CreateCoupon />,
  }[modal.type];

  res.body = <PlanManager csvPress={csvPress} />;
};

export default PlanManagerController;
